// Accessibility service for font size, text-to-speech, and themes
import { reactive, watch } from 'vue'

const STORAGE_KEY = 'accessibility_settings'

// Default settings
const defaultSettings = {
  fontSize: 'medium', // 'small', 'medium', 'large', 'xlarge', 'xxlarge', 'xxxlarge'
  highContrast: false,
  textToSpeechEnabled: false,
  readerModeEnabled: false, // Show floating reader mode button
  translateTo: '', // Empty means no auto-translate, otherwise language code like 'en', 'da', 'th'
  theme: 'default', // 'default', 'twitter', 'facebook', 'purple', 'warm', 'dark', 'twitterDark', 'highContrast', 'nordic'
  favoriteGroupId: null
}

// Load from localStorage
const loadSettings = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : { ...defaultSettings }
  } catch {
    return { ...defaultSettings }
  }
}

// Reactive settings
export const accessibilitySettings = reactive(loadSettings())

// Save settings when changed
watch(accessibilitySettings, (newSettings) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings))
  applySettings()
}, { deep: true })

// Font size mappings
const fontSizeMap = {
  small: {
    base: '14px',
    scale: 0.875
  },
  medium: {
    base: '16px',
    scale: 1
  },
  large: {
    base: '18px',
    scale: 1.125
  },
  xlarge: {
    base: '20px',
    scale: 1.25
  },
  xxlarge: {
    base: '24px',
    scale: 1.5
  },
  xxxlarge: {
    base: '28px',
    scale: 1.75
  }
}

// Apply settings to document
export function applySettings() {
  const root = document.documentElement
  const body = document.body
  
  // Apply font size
  const fontSize = fontSizeMap[accessibilitySettings.fontSize] || fontSizeMap.medium
  root.style.fontSize = fontSize.base
  body.style.setProperty('--a11y-font-scale', fontSize.scale)
  
  // Apply high contrast
  if (accessibilitySettings.highContrast) {
    body.classList.add('high-contrast-theme')
  } else {
    body.classList.remove('high-contrast-theme')
  }
}

// Text-to-speech functions
export function speak(text, lang = 'da-DK') {
  if (!accessibilitySettings.textToSpeechEnabled) return
  if (!('speechSynthesis' in window)) {
    console.warn('Text-to-speech not supported')
    return
  }
  
  // Cancel any ongoing speech
  window.speechSynthesis.cancel()
  
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = lang
  utterance.rate = 0.9
  utterance.pitch = 1
  
  window.speechSynthesis.speak(utterance)
}

export function stopSpeaking() {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel()
  }
}

export function speakElement(element) {
  if (!element) return
  const text = element.innerText || element.textContent
  if (text) {
    speak(text.trim())
  }
}

// Cycle through font sizes
export function cycleFontSize() {
  const sizes = ['small', 'medium', 'large', 'xlarge', 'xxlarge', 'xxxlarge']
  const currentIndex = sizes.indexOf(accessibilitySettings.fontSize)
  const nextIndex = (currentIndex + 1) % sizes.length
  accessibilitySettings.fontSize = sizes[nextIndex]
  return accessibilitySettings.fontSize
}

// Toggle high contrast
export function toggleHighContrast() {
  accessibilitySettings.highContrast = !accessibilitySettings.highContrast
  return accessibilitySettings.highContrast
}

// Toggle text-to-speech
export function toggleTextToSpeech() {
  accessibilitySettings.textToSpeechEnabled = !accessibilitySettings.textToSpeechEnabled
  if (!accessibilitySettings.textToSpeechEnabled) {
    stopSpeaking()
  }
  return accessibilitySettings.textToSpeechEnabled
}

// Initialize on load
export function initAccessibility() {
  applySettings()
}

// Export for use in components
export default {
  settings: accessibilitySettings,
  applySettings,
  speak,
  stopSpeaking,
  speakElement,
  cycleFontSize,
  toggleHighContrast,
  toggleTextToSpeech,
  initAccessibility
}